// PRODUCTION — admin flow editor
import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

/** How long to wait for the popstate of `dropGuardHistoryEntries` before giving up on it. */
const GUARD_HISTORY_DROP_TIMEOUT_MS = 1000

interface UseUnsavedChangesGuardResult {
  showUnsavedModal: boolean
  guardNavigate: (path: string) => void
  confirmLeave: () => void
  cancelLeave: () => void
  dropGuardHistoryEntries: () => Promise<void>
}

export const useUnsavedChangesGuard = (isDirty: boolean): UseUnsavedChangesGuardResult => {
  const navigate = useNavigate()
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingPath, setPendingPath] = useState<string | null>(null)
  // How many duplicate entries the back-button block below has pushed onto the history stack.
  const pushedGuardEntriesRef = useRef(0)
  // Set while `dropGuardHistoryEntries` unwinds those entries on purpose, so the popstate it
  // causes is not mistaken for the user pressing Back.
  const droppingGuardEntriesRef = useRef(false)

  // Block tab close / refresh
  useEffect(() => {
    if (!isDirty) {
      return
    }

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)

    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Block browser back button
  useEffect(() => {
    if (!isDirty) {
      return
    }

    window.history.pushState(null, '', window.location.href)
    pushedGuardEntriesRef.current += 1
    const handlePopstate = () => {
      // A deliberate unwind (see `dropGuardHistoryEntries`) must not re-push or prompt.
      if (droppingGuardEntriesRef.current) {
        droppingGuardEntriesRef.current = false

        return
      }

      window.history.pushState(null, '', window.location.href)
      setPendingPath(null)
      setShowUnsavedModal(true)
    }
    window.addEventListener('popstate', handlePopstate)

    return () => window.removeEventListener('popstate', handlePopstate)
  }, [isDirty])

  /**
   * Remove the duplicate history entries pushed by the back-button block, resolving once the
   * browser has applied the change.
   *
   * Those entries carry the editor's own URL, so a caller that then navigates with `replace` would
   * otherwise only overwrite a duplicate and leave the entry the user actually arrived on behind
   * (see the post-create navigation in `useFlowEditorContextComposer`). Dropping them first makes
   * `replace` land on the real entry. Resolves immediately when nothing was pushed.
   */
  const dropGuardHistoryEntries = useCallback((): Promise<void> => {
    const pushedEntries = pushedGuardEntriesRef.current

    if (pushedEntries === 0) {
      return Promise.resolve()
    }

    pushedGuardEntriesRef.current = 0
    droppingGuardEntriesRef.current = true

    return new Promise<void>((resolve) => {
      const handleDropped = () => {
        window.removeEventListener('popstate', handleDropped)
        clearTimeout(timeoutId)
        droppingGuardEntriesRef.current = false
        resolve()
      }
      // A browser that never delivers the popstate (or a history stack shorter than expected)
      // must not hang the caller's save.
      const timeoutId = setTimeout(() => {
        window.removeEventListener('popstate', handleDropped)
        droppingGuardEntriesRef.current = false
        resolve()
      }, GUARD_HISTORY_DROP_TIMEOUT_MS)

      // Registered after the block's own popstate listener, so that one runs first and skips.
      window.addEventListener('popstate', handleDropped)
      window.history.go(-pushedEntries)
    })
  }, [])

  const guardNavigate = useCallback(
    (path: string) => {
      if (isDirty) {
        setPendingPath(path)
        setShowUnsavedModal(true)
      } else {
        navigate(path)
      }
    },
    [isDirty, navigate],
  )

  const confirmLeave = useCallback(() => {
    setShowUnsavedModal(false)

    if (pendingPath) {
      navigate(pendingPath)
    } else {
      navigate(-1)
    }
  }, [pendingPath, navigate])

  const cancelLeave = useCallback(() => {
    setShowUnsavedModal(false)
    setPendingPath(null)
  }, [])

  return {
    showUnsavedModal,
    guardNavigate,
    confirmLeave,
    cancelLeave,
    dropGuardHistoryEntries,
  }
}
